/**
 * La calculatrice, ecrite comme une fonction pure.
 *
 * L'etat est une donnee, la frappe est une transition: `appuyer(etat, touche)`.
 * Rien de tout cela ne connait le DOM, donc tout se teste sans navigateur — et
 * le panneau n'a plus qu'a dessiner des boutons.
 *
 * Ecriture quebecoise: la VIRGULE decimale. Le point n'existe pas sur le
 * clavier de la calculatrice; il est accepte a la lecture (voir notation.ts).
 */
import { formaterNombre } from './notation.ts'

export type Operation = '+' | '−' | '×' | '÷'

export type ToucheCalc =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | ','
  | '+'
  | '−'
  | '×'
  | '÷'
  | '='
  | 'C'
  | 'retour'
  | 'signe'
  | '%'

export interface EtatCalc {
  /** Ce qui est ecrit en gros. Toujours une chaine: « 0 », « 12,5 », « Erreur ». */
  affichage: string
  /** La valeur mise de cote par la derniere operation. */
  memoire: number
  operation: Operation | null
  /** Vrai quand le prochain chiffre recommence l'affichage. */
  neuf: boolean
  /** Le rappel discret sous l'affichage: « 12 + ». */
  rappel: string
}

/** Au plus douze chiffres: au-dela, l'affichage deborde sur un telephone. */
const MAX_CHIFFRES = 12

export function etatCalcNeuf(): EtatCalc {
  return { affichage: '0', memoire: 0, operation: null, neuf: true, rappel: '' }
}

/** La valeur affichee, ou `null` si l'affichage porte une erreur. */
export function valeurAffichee(e: EtatCalc): number | null {
  const v = Number(e.affichage.replace(',', '.'))
  return Number.isFinite(v) ? v : null
}

function ecrire(v: number): string {
  if (!Number.isFinite(v)) return 'Erreur'
  const t = formaterNombre(v, 8)
  return t.length > MAX_CHIFFRES + 2 ? 'Erreur' : t
}

function appliquer(gauche: number, op: Operation, droite: number): number {
  if (op === '+') return gauche + droite
  if (op === '−') return gauche - droite
  if (op === '×') return gauche * droite
  return droite === 0 ? Number.NaN : gauche / droite
}

const CHIFFRES = '0123456789'

/**
 * Une frappe. Rend TOUJOURS un nouvel etat: rien n'est modifie sur place, ce
 * qui rend la comparaison avant/apres triviale dans un test.
 */
export function appuyer(e: EtatCalc, t: ToucheCalc): EtatCalc {
  if (t === 'C') return etatCalcNeuf()

  if (e.affichage === 'Erreur') {
    // Apres une erreur, seule une touche de chiffre repart de zero.
    if (!CHIFFRES.includes(t)) return e
    return { affichage: t, memoire: 0, operation: null, neuf: false, rappel: '' }
  }

  if (CHIFFRES.includes(t)) {
    if (e.neuf) return { ...e, affichage: t, neuf: false }
    if (e.affichage === '0') return { ...e, affichage: t }
    if (e.affichage.replace(/[^0-9]/g, '').length >= MAX_CHIFFRES) return e
    return { ...e, affichage: e.affichage + t }
  }

  if (t === ',') {
    if (e.neuf) return { ...e, affichage: '0,', neuf: false }
    return e.affichage.includes(',') ? e : { ...e, affichage: `${e.affichage},` }
  }

  if (t === 'retour') {
    if (e.neuf) return e
    const coupe = e.affichage.slice(0, -1)
    return { ...e, affichage: coupe === '' || coupe === '-' ? '0' : coupe, neuf: coupe === '' }
  }

  if (t === 'signe') {
    const v = valeurAffichee(e)
    if (v === null) return e
    return { ...e, affichage: ecrire(-v) }
  }

  if (t === '%') {
    const v = valeurAffichee(e)
    if (v === null) return e
    return { ...e, affichage: ecrire(v / 100), neuf: true }
  }

  const v = valeurAffichee(e)
  if (v === null) return e

  if (t === '=') {
    if (e.operation === null) return { ...e, neuf: true, rappel: '' }
    const r = appliquer(e.memoire, e.operation, v)
    return { affichage: ecrire(r), memoire: 0, operation: null, neuf: true, rappel: '' }
  }

  // Les chiffres et les touches speciales sont deja partis: il ne reste qu'une
  // operation. Le test explicite est ce qui l'apprend au verificateur de types.
  if (t !== '+' && t !== '−' && t !== '×' && t !== '÷') return e

  // Une operation: on referme d'abord celle qui attendait.
  const gauche = e.operation === null || e.neuf ? v : appliquer(e.memoire, e.operation, v)
  const texte = ecrire(gauche)
  if (texte === 'Erreur') return { affichage: 'Erreur', memoire: 0, operation: null, neuf: true, rappel: '' }
  return { affichage: texte, memoire: gauche, operation: t, neuf: true, rappel: `${texte} ${t}` }
}
